import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import { createPortal } from "react-dom";
import useResizeObserver from "use-resize-observer";

import CrossIcon from "@components/UI/Icons/Cross";

let modalRoot: HTMLElement = null;
if (typeof window !== "undefined") {
  modalRoot = document.getElementById("modal");
}

interface IProps {
  children: React.ReactNode;
  isActive: boolean;
  className?: string;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
  onClose?: () => void;
  modalTitle?: string;
  modalTitleClassName?: string;
  setShowVerticalScrollBar?: Dispatch<SetStateAction<boolean>>;
  setShowHorizontalScrollBar?: Dispatch<SetStateAction<boolean>>;
  isCloseButtonHidden?: boolean;
  disableOutsideClickClose?: boolean;
  initialFocusSelector?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  closeButtonAriaLabel?: string;
}

const FOCUSABLE_ELEMENT_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

const isVisibleElement = (element: HTMLElement) =>
  !!(
    element.offsetWidth ||
    element.offsetHeight ||
    element.getClientRects().length
  );

const getFocusableElements = (element: HTMLElement): HTMLElement[] =>
  Array.from(
    element.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR)
  ).filter(
    (focusableElement) =>
      focusableElement.getAttribute("aria-hidden") !== "true" &&
      isVisibleElement(focusableElement)
  );

const setShowScrollbars = (
  elements: { verticalElem: HTMLElement; horizontalElem: HTMLElement },
  setShowVerticalScrollBar: Dispatch<SetStateAction<boolean>>,
  setShowHorizontalScrollBar: Dispatch<SetStateAction<boolean>>
) => {
  const { verticalElem, horizontalElem } = elements;

  if (verticalElem.scrollHeight > verticalElem.offsetHeight) {
    setShowVerticalScrollBar(true);
  } else {
    setShowVerticalScrollBar(false);
  }

  if (horizontalElem.scrollWidth > verticalElem.offsetWidth) {
    setShowHorizontalScrollBar(true);
  } else {
    setShowHorizontalScrollBar(false);
  }
};

const Modal: React.FC<IProps> = ({
  isActive,
  className,
  innerClassName,
  innerStyle,
  onClose,
  modalTitle,
  modalTitleClassName,
  setShowVerticalScrollBar,
  setShowHorizontalScrollBar,
  children,
  isCloseButtonHidden,
  disableOutsideClickClose,
  initialFocusSelector,
  ariaLabelledBy,
  ariaDescribedBy,
  closeButtonAriaLabel = "Close",
}) => {
  const modalInnerElement = React.useRef<HTMLDivElement>(null);
  const modalContentElement = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = React.useRef<HTMLElement | null>(null);
  const onCloseRef = React.useRef(onClose);

  const { width, height } = useResizeObserver<HTMLDivElement>({
    ref: modalInnerElement,
  });

  React.useEffect(() => {
    if (
      modalInnerElement.current instanceof HTMLElement &&
      modalContentElement.current instanceof HTMLElement &&
      setShowHorizontalScrollBar &&
      setShowVerticalScrollBar
    ) {
      setShowScrollbars(
        {
          verticalElem: modalInnerElement.current,
          horizontalElem: modalContentElement.current,
        },
        setShowVerticalScrollBar,
        setShowHorizontalScrollBar
      );
    }
  }, [width, height, setShowVerticalScrollBar, setShowHorizontalScrollBar]);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const isTopModal = React.useCallback(() => {
    if (!modalRoot || !modalInnerElement.current) {
      return true;
    }

    const modalInnerElements = document.getElementsByClassName("modal-inner");
    const lastElement = modalInnerElements[modalRoot.childElementCount - 1];

    return !lastElement || modalInnerElement.current.isSameNode(lastElement);
  }, []);

  React.useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    previouslyFocusedElement.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusInitialElement = () => {
      if (!modalInnerElement.current) {
        return;
      }

      const initialFocusElement = initialFocusSelector
        ? modalInnerElement.current.querySelector<HTMLElement>(
            initialFocusSelector
          )
        : null;
      const focusableElements = getFocusableElements(modalInnerElement.current);

      (
        initialFocusElement ||
        focusableElements[0] ||
        modalInnerElement.current
      ).focus();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isTopModal()) {
        return;
      }

      if (e.key === "Escape" && onCloseRef.current) {
        onCloseRef.current();
        return;
      }

      if (e.key !== "Tab" || !modalInnerElement.current) {
        return;
      }

      const focusableElements = getFocusableElements(modalInnerElement.current);

      if (focusableElements.length === 0) {
        e.preventDefault();
        modalInnerElement.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        e.shiftKey &&
        (document.activeElement === firstElement ||
          !modalInnerElement.current.contains(document.activeElement))
      ) {
        e.preventDefault();
        lastElement.focus();
        return;
      }

      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    const focusTimer = window.setTimeout(focusInitialElement, 0);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow || "scroll";
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);

      if (
        previouslyFocusedElement.current &&
        document.contains(previouslyFocusedElement.current)
      ) {
        previouslyFocusedElement.current.focus();
      }
      previouslyFocusedElement.current = null;
    };
  }, [initialFocusSelector, isActive, isTopModal]);

  const handleModalClick = (e: React.MouseEvent) => {
    const elBg = document.getElementsByClassName("modal-inner");

    const lastElement = elBg[modalRoot.childElementCount - 1];
    if (
      !onClose ||
      disableOutsideClickClose ||
      (e.target instanceof Node &&
        modalInnerElement.current.contains(e.target)) ||
      (modalRoot.childElementCount > 1 &&
        !modalInnerElement.current.isSameNode(lastElement))
    ) {
      return;
    }

    onClose();
  };

  if (!isActive || modalRoot === null) {
    return null;
  }

  return createPortal(
    <div
      role="presentation"
      className={`modal-root ${isActive ? "-active" : ""} ${className}`}
      onClick={handleModalClick}
    >
      <div className="modal-bg">
        <div
          className={`modal-inner ${innerClassName}`}
          ref={modalInnerElement}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          style={innerStyle}
          tabIndex={-1}
        >
          {!isCloseButtonHidden && (
            <button
              className="modal-close-btn"
              onClick={() => {
                onClose?.();
              }}
              type="button"
              aria-label={closeButtonAriaLabel}
            >
              <CrossIcon size="sm" color="dark-gray" />
            </button>
          )}

          {modalTitle ? (
            <h2
              className={`modal-title ${
                typeof modalTitleClassName === "string"
                  ? `modal-title--${modalTitleClassName}`
                  : ""
              }`}
            >
              {modalTitle}
            </h2>
          ) : null}
          <div className="modal-content" ref={modalContentElement}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default Modal;
